// The fuel depot, made for a phone in the yard: issuing fuel to a truck (pump meter before and after,
// odometer), receiving a bulk delivery (dips before and after), dipping the tanks, fuel bought outside
// (waits for approval), recent issues with their flags, and the stock check.
import { useState } from "react";
import { Page, Card, Btn, Tabs, Table, Tr, Td, Field, Loading } from "@/components/kit";
import api from "@/lib/api";
import { act, DateIn, input, k0, n, Num, opts, Select, State, thisMonth, useCan, useLoad, useRegisters } from "@/components/erp";
import { DepotTable } from "@/pages/Dashboards";
import { fmtDate, today } from "@/lib/money";

export default function FuelDepot() {
  const can = useCan();
  const tabs = [
    can("fuel.issue") && { value: "issue", label: "Issue fuel" },
    can("fuel.receive") && { value: "receive", label: "Receive delivery" },
    can("fuel.dip") && { value: "dip", label: "Dip tanks" },
    can("fuel.outside.request") && { value: "outside", label: "Fuel bought outside" },
    { value: "recent", label: "Recent & stock check" },
  ].filter(Boolean);
  const [tab, setTab] = useState(tabs[0].value);
  const regs = useRegisters(["trucks", "tanks", "drivers"]);
  return (
    <Page title="Fuel depot">
      <Tabs value={tab} onChange={setTab} tabs={tabs} />
      {tab === "issue" && <Issue regs={regs} />}
      {tab === "receive" && <Receive regs={regs} />}
      {tab === "dip" && <Dip regs={regs} />}
      {tab === "outside" && <Outside regs={regs} />}
      {tab === "recent" && <Recent />}
    </Page>
  );
}

const big = "min-h-[48px] text-base";

function Issue({ regs }) {
  const blank = { date: today(), source: "depot", purpose: "trip" };
  const [f, setF] = useState(blank);
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const tanks = regs.tanks || [];
  const metered = n(f.pump_end) != null && n(f.pump_start) != null ? n(f.pump_end) - n(f.pump_start) : null;
  const tank_id = f.tank_id || (tanks.length === 1 ? tanks[0].id : null);
  const truck = (regs.trucks || []).find((t) => t.id === f.truck_id);
  const noOdo = truck?.odometer_ok === false;
  return (
    <Card className="mt-4 max-w-xl bg-white">
      <div className="grid gap-3">
        <Field label="Truck"><Select className={big} value={f.truck_id} onChange={set("truck_id")} options={opts(regs.trucks, "plate")} /></Field>
        {tanks.length > 1 && <Field label="Depot tank"><Select className={big} value={f.tank_id} onChange={set("tank_id")} options={opts(tanks)} /></Field>}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Pump meter before"><Num value={f.pump_start} onChange={set("pump_start")} /></Field>
          <Field label="Pump meter after"><Num value={f.pump_end} onChange={(v) => setF({ ...f, pump_end: v, litres: n(f.pump_start) != null && n(v) != null ? (n(v) - n(f.pump_start)).toFixed(1) : f.litres })} /></Field>
        </div>
        <Field label="Litres"><Num value={f.litres} onChange={set("litres")} /></Field>
        {metered != null && n(f.litres) != null && Math.abs(metered - n(f.litres)) > 0.5 && <p className="text-sm text-rose-700">The pump meter shows {metered.toFixed(1)} L</p>}
        {noOdo ? <p className="text-sm text-amber-700">This truck's odometer doesn't work - its distance comes from the routes it runs.</p>
          : <Field label="Truck's odometer (km)"><Num value={f.odometer} onChange={set("odometer")} /></Field>}
        <Field label="Driver"><Select className={big} value={f.driver_id} onChange={set("driver_id")} options={opts(regs.drivers)} /></Field>
        <Field label="Date"><DateIn value={f.date} onChange={set("date")} /></Field>
      </div>
      <p className="mt-2 text-xs text-slate-500">The fuel goes to the truck's open trip.</p>
      <Btn kind="primary" className="mt-4 w-full justify-center py-3 text-base" disabled={!f.truck_id || !n(f.litres) || (!noOdo && n(f.odometer) == null)}
        onClick={async () => {
          const d = await act(() => api.post("/costs/fuel/issues", { ...f, tank_id, litres: n(f.litres), pump_start: n(f.pump_start), pump_end: n(f.pump_end), odometer: n(f.odometer) }),
            (x) => (x.flags?.length ? `Recorded - flagged: ${x.flags.join("; ")}` : `Issued ${k0(x.litres)} L at ${x.price}/L`));
          if (d) setF({ ...blank, pump_start: f.pump_end });
        }}>Issue fuel</Btn>
    </Card>
  );
}

function Receive({ regs }) {
  const [f, setF] = useState({ date: today() });
  const set = (k) => (v) => setF({ ...f, [k]: v });
  return (
    <Card title="Bulk delivery into the depot" className="mt-4 max-w-xl bg-white">
      <div className="mt-3 grid gap-3">
        <Field label="Tank"><Select className={big} value={f.tank_id} onChange={set("tank_id")} options={opts(regs.tanks)} /></Field>
        <Field label="Supplier"><input className={input} value={f.supplier || ""} onChange={(e) => set("supplier")(e.target.value)} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Litres (invoice)"><Num value={f.litres} onChange={set("litres")} /></Field>
          <Field label="Cost (KES, incl. VAT)"><Num value={f.amount} onChange={set("amount")} /></Field>
          <Field label="Dip before (L)"><Num value={f.dip_before} onChange={set("dip_before")} /></Field>
          <Field label="Dip after (L)"><Num value={f.dip_after} onChange={set("dip_after")} /></Field>
        </div>
        <Field label="Invoice no."><input className={input} value={f.invoice_no || ""} onChange={(e) => set("invoice_no")(e.target.value)} /></Field>
        <Field label="Date"><DateIn value={f.date} onChange={set("date")} /></Field>
      </div>
      <Btn kind="primary" className="mt-4" disabled={!f.tank_id || !n(f.litres) || !n(f.amount)} onClick={async () => {
        const d = await act(() => api.post("/costs/fuel/receipts", { ...f, litres: n(f.litres), amount: n(f.amount), dip_before: n(f.dip_before), dip_after: n(f.dip_after) }),
          (x) => (x.short_l > 1 ? `Received - ${k0(x.short_l)} L less went in than invoiced` : "Received"));
        if (d) setF({ date: today() });
      }}>Save delivery</Btn>
    </Card>
  );
}

function Dip({ regs }) {
  const [f, setF] = useState({ date: today() });
  return (
    <Card title="Tank dip" className="mt-4 max-w-md bg-white">
      <div className="mt-3 grid gap-3">
        <Field label="Tank"><Select className={big} value={f.tank_id} onChange={(v) => setF({ ...f, tank_id: v })} options={opts(regs.tanks)} /></Field>
        <Field label="Litres in the tank"><Num value={f.litres} onChange={(v) => setF({ ...f, litres: v })} /></Field>
        <Field label="Date"><DateIn value={f.date} onChange={(v) => setF({ ...f, date: v })} /></Field>
      </div>
      <Btn kind="primary" className="mt-4" disabled={!f.tank_id || n(f.litres) == null} onClick={async () => {
        if (await act(() => api.post("/costs/fuel/dips", { ...f, litres: n(f.litres) }), "Dip recorded")) setF({ date: today() });
      }}>Save dip</Btn>
    </Card>
  );
}

function Outside({ regs }) {
  const [f, setF] = useState({ date: today(), source: "outside", purpose: "en_route" });
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const noOdo = (regs.trucks || []).find((t) => t.id === f.truck_id)?.odometer_ok === false;
  return (
    <Card title="Fuel bought outside the depot" className="mt-4 max-w-xl bg-white">
      <p className="mt-1 text-sm text-slate-600">Only when the depot can't supply. It waits for approval and counts once approved.</p>
      <div className="mt-3 grid gap-3">
        <Field label="Truck"><Select className={big} value={f.truck_id} onChange={set("truck_id")} options={opts(regs.trucks, "plate")} /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Litres"><Num value={f.litres} onChange={set("litres")} /></Field>
          <Field label="Price per litre"><Num value={f.price} onChange={set("price")} /></Field>
        </div>
        <Field label="Station / where"><input className={input} value={f.station || ""} onChange={(e) => set("station")(e.target.value)} /></Field>
        <Field label="Why not from the depot"><input className={input} value={f.reason || ""} onChange={(e) => set("reason")(e.target.value)} /></Field>
        {!noOdo && <Field label="Truck's odometer (km)"><Num value={f.odometer} onChange={set("odometer")} /></Field>}
        <Field label="Receipt no."><input className={input} value={f.receipt_no || ""} onChange={(e) => set("receipt_no")(e.target.value)} /></Field>
        <Field label="Date"><DateIn value={f.date} onChange={set("date")} /></Field>
      </div>
      {n(f.litres) && n(f.price) ? <p className="mt-2 text-sm">Total: <b>KES {k0(n(f.litres) * n(f.price))}</b></p> : null}
      <Btn kind="primary" className="mt-4" disabled={!f.truck_id || !n(f.litres) || !n(f.price) || !f.station || !f.reason || (!noOdo && n(f.odometer) == null)} onClick={async () => {
        if (await act(() => api.post("/costs/fuel/issues", { ...f, litres: n(f.litres), price: n(f.price), odometer: n(f.odometer) }), "Sent for approval")) setF({ date: today(), source: "outside", purpose: "en_route" });
      }}>Submit for approval</Btn>
    </Card>
  );
}

function Recent() {
  const p = thisMonth();
  const { data } = useLoad("/costs/fuel/issues", p);
  const { data: depot } = useLoad("/costs/fuel/depot", p);
  return (
    <>
      {depot && <DepotTable rows={depot} />}
      <Card title="Fuel issued this month" className="mt-5 bg-white">
        {!data ? <Loading /> : (
          <Table minWidth={760} cols={[{ label: "Date" }, { label: "Truck" }, { label: "Where" }, { label: "Litres", align: "right" }, { label: "Pump", align: "right" }, { label: "Odometer", align: "right" }, { label: "" }]}>
            {data.map((f) => (
              <Tr key={f.id} className={f.flags?.length ? "bg-rose-50" : ""}>
                <Td>{fmtDate(f.date)}</Td><Td>{f.truck}</Td><Td>{f.source === "outside" ? f.station : "Depot"}</Td><Td right>{k0(f.litres)}</Td>
                <Td right>{f.pump_start != null ? `${k0(f.pump_start)}–${k0(f.pump_end)}` : ""}</Td><Td right>{k0(f.odometer)}</Td>
                <Td><State s={f.status} />{f.flags?.length ? <div className="text-xs text-rose-700">{f.flags.join("; ")}</div> : null}</Td>
              </Tr>
            ))}
          </Table>
        )}
      </Card>
    </>
  );
}
